import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  ClipboardList,
  Users,
  Repeat,
  Receipt,
  Briefcase,
  Clock,
  Landmark,
  Settings,
  LogOut,
  ChevronsUpDown,
  Building2,
  BookOpen,
  BookText,
  BarChart3,
  ShieldCheck,
  UserCog,
  KeyRound,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" }],
  },
  {
    label: "Bookkeeping",
    items: [
      { to: "/invoices", label: "Invoices", icon: FileText, id: "invoices" },
      { to: "/purchase-orders", label: "Purchase Orders", icon: ClipboardList, id: "purchase-orders" },
      { to: "/clients", label: "Clients", icon: Users, id: "clients" },
      { to: "/recurring", label: "Recurring Income", icon: Repeat, id: "recurring" },
      { to: "/expenses", label: "Expenses", icon: Receipt, id: "expenses" },
      { to: "/jobs", label: "Jobs & Profitability", icon: Briefcase, id: "jobs" },
      { to: "/time", label: "Time", icon: Clock, id: "time" },
    ],
  },
  {
    label: "Accounting",
    items: [
      { to: "/accounts", label: "Chart of Accounts", icon: BookOpen, id: "accounts" },
      { to: "/journal", label: "Journal Entries", icon: BookText, id: "journal" },
      { to: "/reports", label: "Reports", icon: BarChart3, id: "reports" },
      { to: "/gst", label: "GST Return", icon: Landmark, id: "gst" },
    ],
  },
  {
    label: "Manage",
    items: [
      { to: "/team", label: "Users & Access", icon: UserCog, id: "team" },
      { to: "/settings", label: "Company Settings", icon: Settings, id: "settings" },
    ],
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { companies, current, currentId, switchCompany } = useCompany();
  const navigate = useNavigate();

  const initials = (user?.name || "U")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-slate-200 bg-white flex flex-col fixed h-screen">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-slate-200">
          <div className="h-8 w-8 rounded-md bg-slate-900 flex items-center justify-center">
            <Landmark className="h-4 w-4 text-white" />
          </div>
          <div className="font-heading font-bold text-slate-900 leading-tight">
            Ledgerly
            <div className="text-[10px] font-normal text-slate-400 tracking-wide">
              BOOKKEEPING · ALBERTA
            </div>
          </div>
        </div>

        {/* Company switcher */}
        <div className="p-3 border-b border-slate-200">
          <label className="text-[11px] uppercase tracking-wide text-slate-400 px-1">
            Active Company
          </label>
          <Select value={currentId || ""} onValueChange={switchCompany}>
            <SelectTrigger
              data-testid="company-switcher"
              className="mt-1 rounded-md border-slate-200 bg-slate-50"
            >
              <div className="flex items-center gap-2 truncate">
                <Building2 className="h-4 w-4 text-slate-500 shrink-0" />
                <SelectValue placeholder="Select company" />
              </div>
            </SelectTrigger>
            <SelectContent className="bg-white">
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`company-option-${c.id}`}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <nav className="flex-1 p-3 space-y-4 overflow-y-auto">
          {NAV_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                {group.label}
              </div>
              <div className="space-y-0.5">
                {group.items.map(({ to, label, icon: Icon, end, id }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    data-testid={`nav-${id}`}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150",
                        isActive
                          ? "bg-slate-900 text-white"
                          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                      )
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
          {user?.is_admin && (
            <div>
              <div className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Administration
              </div>
              <NavLink
                to="/admin"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150",
                    isActive
                      ? "bg-slate-900 text-white"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  )
                }
              >
                <ShieldCheck className="h-4 w-4" />
                Admin Console
              </NavLink>
            </div>
          )}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-slate-100 transition-colors"
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-slate-900 text-white text-xs">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="text-left flex-1 truncate">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {user?.name}
                  </div>
                  <div className="text-[11px] text-slate-400 truncate">{user?.email}</div>
                </div>
                <ChevronsUpDown className="h-4 w-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 bg-white" align="end">
              <DropdownMenuLabel>{user?.name}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="account-menu-item" onClick={() => navigate("/account")}>
                <KeyRound className="h-4 w-4 mr-2" /> My account &amp; password
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="logout-btn"
                onClick={async () => {
                  await logout();
                  navigate("/login");
                }}
              >
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-64 min-w-0">
        <div className="sticky top-0 z-40 h-16 backdrop-blur-md bg-white/80 border-b border-slate-200 flex items-center px-8">
          <div className="text-sm text-slate-500">
            {current ? (
              <span>
                Viewing <span className="font-semibold text-slate-900">{current.name}</span>
                <span className="mx-2 text-slate-300">·</span>
                <span className="capitalize">{current.filing_frequency} filing</span>
              </span>
            ) : (
              "Loading…"
            )}
          </div>
        </div>
        <div className="p-8 max-w-[1400px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
